import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, RefObject, Dispatch, SetStateAction } from 'react';
import { Socket } from 'socket.io-client';
import {socket} from '../socket';
import { ToastContext } from './ToastContext';
import {useNavigate} from 'react-router-dom';
import { BlockTradeEvent, Card, DealerAction, DealerChoiceEvent, EventLog, FlipAllCardsEvent, GameConfig, GamePlayerInfo, GameResetReturn, GameStartedReturn, InitialConnect, InitialConnectReturn, JoinRoomPayload, NewGameRoundReturn, PlayerAction, PlayerChoiceEvent, PlayerEmotePayload, PlayerEmoteSlug, PlayerFacingDirection, PlayerFacingPayload, PublicGamePlayerInfo, PublicGamePlayersState, PublicGameSession, ResultModalType, RoomCreatedReturn, RoomState, ServerRoundEventPayload, ServerRoundEvents, ServerStartGamePayload, socketKeys, SpectatorState, SuitImages, TradeCardsEvent, UnknownRoundEvent, UserDisconnectedReturn, UserJoinedReturn, UserLeftReturn, UserState, UserUpdatedReturn } from '../types/SharedTypes';
import { useStateRef } from '../hooks/useStateRef';
import SoundManager from '../tools/SoundManager';

// Define the shape of the context value
interface SocketContextType {
  socket: Socket | null;
  user: UserState | null;
  isInitializing: boolean;
  room: RoomState | null;
  game: PublicGameSession | null;
  currentPlayer: PublicGamePlayerInfo | null;
  currentPlayerRef: RefObject<PublicGamePlayerInfo | null>;
  currentCard: Card | null;
  gameConfig: GameConfig;
  setGameConfig: Dispatch<SetStateAction<GameConfig>>;
  yourTurn: boolean;
  isDealer: boolean;
  isNextDealer: boolean;
  currentDealer: PublicGamePlayerInfo | null;
  isCuttingDeck: boolean;
  setIsCuttingDeck: (prev: boolean) => void;
  players: PublicGamePlayerInfo[];
  allPlayerInfoRef: RefObject<PublicGamePlayersState>;
  spectators: SpectatorState[];
  currentLosers: GamePlayerInfo[];
  winner: GamePlayerInfo | null;
  resultModal: ResultModalType;
  setResultModal: (prev: ResultModalType) => void;
  passwordModal: string | false;
  setPasswordModal: (prev: string | false) => void;
  suitImages: SuitImages | null;
  setSuitImages: (prev: SuitImages | null) => void;
  eventLogs: EventLog[];
  recentEventLogs: EventLog[];
  joinIntention: string | null;
  setJoinIntention: (prev: string | null) => void;
  createRoom: (password?: string) => void;
  joinRoom: (code: string, password?: string) => void;
  rejoinRoom: () => void;
  leaveRoom: () => void;
  startGame: () => void;
  dealCards: () => void;
  makeGameChoice: (choice: PlayerAction | DealerAction, cutPosition?: number) => void;
  addBotToRoom: () => void;
  kickPlayer: (kickUserId: string) => void;
  updateUserFacing: (direction: PlayerFacingDirection) => void;
  sendEmote: (emote: PlayerEmoteSlug) => void;
}

// Create the context with an initial value of `null`
const SocketContext = createContext<SocketContextType | undefined>(undefined);

const DISCONNECT_TIMER = 30000;
const TURN_TIMER = 30000;

// Define the type for the provider's children prop
interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {

  const {requestToast} = useContext(ToastContext);
  const navigate = useNavigate();

  const [user, setUser, userRef] = useStateRef<UserState | null>(null);
  const [currentPlayer, setCurrentPlayer, currentPlayerRef] = useStateRef<PublicGamePlayerInfo | null>(null);
  const [currentCard, setCurrentCard, currentCardRef] = useStateRef<Card | null>(null);
  const [room, setRoom, roomRef] = useStateRef<RoomState | null>(null);
  const [game, setGame, gameRef] = useStateRef<PublicGameSession | null>(null);
  const [eventLogs, setEventLogs] = useState<EventLog[]>([]);
  const [recentEventLogs, setRecentEventLogs] = useState<EventLog[]>([]);
  const [joinIntention, setJoinIntention] = useState<string|null>(null);
  
  const [allPlayerInfo, setAllPlayerInfo, allPlayerInfoRef] = useStateRef<PublicGamePlayersState>({
    players: [], spectators: []
  });
  const [currentLosers, setCurrentLosers] = useState<GamePlayerInfo[]>([]);
  
  const [gameConfig, setGameConfig] = useState<GameConfig>({lives: 3, turnTimer: TURN_TIMER});
  const [yourTurn, setYourTurn, yourTurnRef] = useStateRef(false);
  const [isDealer, setIsDealer, isDealerRef] = useStateRef(false);
  const [isNextDealer, setIsNextDealer, isNextDealerRef] = useStateRef(false);
  const [currentDealer, setCurrentDealer] = useState<PublicGamePlayerInfo | null>(null);
  const [winner, setWinner] = useState<GamePlayerInfo | null>(null);
  const [resultModal, setResultModal] = useState<ResultModalType>(false);
  const [passwordModal, setPasswordModal] = useState<string | false>(false);
  const [suitImages, setSuitImages] = useState<SuitImages | null>(null);
  const [isCuttingDeck, setIsCuttingDeck] = useState(false);

  const [isInitializing, setIsInitializing, isInitializingRef] = useStateRef(true);
  const didCutDeckRef = useRef(false);
  const turnTimerRef = useRef<NodeJS.Timeout>(null);
  const emoteLastSentRef = useRef<number | null>(null);
  
  useEffect(() => {
    console.log("GAME CHANGE", game?.currentState, room?.creator);
    if (!game) return;

    setCurrentDealer(allPlayerInfo.players.find(plyr => plyr.userId === game.currentDealer) || null);
    setGameConfig({lives: game.config.lives, turnTimer: game.config.turnTimer});

    if (room && game && user && game.currentState === 'disconnect_waiting' && room.creator === user.id) {
      //only start the timer if user is the creator; ensures only one
      startDisconnectTimer(game.currentPlayer);
    }
  }, [game]);

  useEffect(() => {
    if (!user) return;
    const current = allPlayerInfo.players.find(pl => pl.userId === user.id);
    //important; card can be null if the players are emitted to the whole room rather than by player
    //if so, we should not override currentCard
    if (!current) return;
    setCurrentPlayer(current);

    if (!current.card) return;
    setCurrentCard(current.card);
  }, [allPlayerInfo]);

  useEffect(() => {
    if (eventLogs.length <= 3) {
      setRecentEventLogs([...eventLogs]);
      return;
    }
    const recent = eventLogs.slice(-3);
    setRecentEventLogs(recent);
  }, [eventLogs]);

  useEffect(() => {
    if (!room) {
      setEventLogs([]);
      setRecentEventLogs([]);
    }
  }, [room]);

  useEffect(() => {
    if (game?.config.turnTimer === 0) return;

    if (yourTurn && (game?.currentState === 'player_decision' || game?.currentState === 'dealer_decision')) {
      startSelfTurnTimer();
    }
    else if (isNextDealer && game?.currentState === 'round_waiting') {
      startSelfTurnTimer();
    }
    else if (turnTimerRef.current) {
      clearTimeout(turnTimerRef.current);
    }
  }, [yourTurn, game?.currentState]);

  useEffect(() => {

    const initialConnect = () => {
      const userId = window.localStorage.getItem('userId') || undefined;
      const prefs = window.localStorage.getItem('userPrefs');
      const payload: InitialConnect = {userId, preferences: prefs ? JSON.parse(prefs) : undefined};
      socket.emit('initialConnect', payload);
    }

    const handleInitialConnect = (data: InitialConnectReturn) => {
      console.log("data", data);
      if (!data) return;
      window.localStorage.setItem('userId', data.id);
      setUser(data);
      setIsInitializing(false);
    }
    
    const handleUpdatedUser = (data: UserUpdatedReturn) => {
      //if there's a room but joinedRoom is null, then must be a kick (if not creator)
      if (roomRef.current && data.joinedRoom === null && roomRef.current.creator !== data.id) {
        leaveRoom(true);
        requestToast('You were kicked from the room.', 'warn');
      }

      window.localStorage.setItem('userPrefs', 
        JSON.stringify({character: data.character, colour: data.colour, name: data.name})
      );

      setUser(prev => prev ? {
        ...prev,
        colour: data.colour,
        character: data.character,
        name: data.name,
        joinedRoom: data.joinedRoom,
        createdRoom: data.createdRoom
      } : data);
    }

    const handleRoomCreated = (data: RoomCreatedReturn) => {
      console.log("room", data);
      setRoom(data.room);
      setAllPlayerInfo(data.players);
      navigate(`/room/${data.room.code}`);
    }
    
    const handleUserJoin = (data: UserJoinedReturn) => {
      console.log("room, join", data);
      const isSelfRejoin = !roomRef.current;
      
      setPasswordModal(false);
      setRoom(data.room);
      //also updates current player and current card
      setAllPlayerInfo(data.players);
      //only needs to be called if its a rejoin but doesnt matter
      clearDisconnectTimer(data.user.id);
      
      //also updates currentDealer
      if (data.game) setGame(data.game);
      if (isSelfRejoin) {
        setIsDealer(data.game?.currentDealer === data.user.id);
        setIsNextDealer(data.nextDealer?.userId === data.user.id)
        setYourTurn(data.game?.currentPlayer === data.user.id);
        navigate(`/room/${data.room.code}`);
      }
    }
    
    const handleUserLeft = (data: UserLeftReturn) => {
      console.log("room, left", data);
      setRoom(data.room);
      if (data.game) setGame(data.game);
      if (data.players) setAllPlayerInfo(data.players);
    }
    
    const handleUserDisconnect = (data: UserDisconnectedReturn) => {
      console.log("room, disconnect", data);
      setRoom(data.room);
      if (data.game) setGame(data.game);
      if (data.players) setAllPlayerInfo(data.players);
    }

    const handleUserExists = (data: InitialConnectReturn) => {
      console.log("user exists", data);
      if (!data.id) return;
      window.localStorage.setItem('userId', data.id);
      setUser(data);
      setIsInitializing(false);
      
      //auto rejoin if the user loads on a /game url and not /join
      //a /join load is handled by joinroom page
      if ((data.createdRoom || data.joinedRoom) && !window.location.pathname.startsWith('/join')) {
        const rejoinData: JoinRoomPayload = {
          userId: data.id,
          roomCode: (data.createdRoom || data.joinedRoom)!
        }
        socket.emit(socketKeys.toServer.room.rejoin, rejoinData);
      }
    }

    const handleError = (error: string) => {
      requestToast(error, 'error');
    }

    const handleAlreadyStarted = () => {
      //TODO: handle join as spectator
      requestToast('Game has already started.', 'error');
    }
    
    const handleStartGame = (data: GameStartedReturn) => {
      console.log("started game", data);
      
      if (!userRef.current || !roomRef.current) return;
      
      setGame(data);
      console.log("game is set", game);
      
      setIsDealer(data.currentDealer === userRef.current.id);
      setYourTurn(data.currentPlayer === userRef.current.id);
    }
    
    const handleNewRound = (data: NewGameRoundReturn) => {
      if (!userRef.current) return;
      console.log("started round", data);
      setGame(data);
      setIsDealer(data.currentDealer === userRef.current.id);
      setYourTurn(data.currentPlayer === userRef.current.id);
      setCurrentLosers([]);
      setIsNextDealer(false);
    }
    
    const handleGameRoundEvent = (_data: UnknownRoundEvent) => {
      if (!userRef.current || !roomRef.current) return;
      switch(_data.event) {
        case('player-choice'): {
          const data = _data as PlayerChoiceEvent;
          console.log("player choice", data);
          setGame(data.game);
          if (data.players) setAllPlayerInfo(data.players);
          setYourTurn(data.data.userId === userRef.current.id);
          setIsDealer(data.game.currentDealer === userRef.current.id);
          break;
        }
        case('dealer-choice'): {
          const data = _data as DealerChoiceEvent;
          console.log("dealer choice", data);
          setGame(data.game);
          if (data.players) setAllPlayerInfo(data.players);
          setYourTurn(data.data.userId === userRef.current.id);
          setIsDealer(data.game.currentDealer === userRef.current.id);
          break;
        }
        case('trade-cards'): {
          const data = _data as TradeCardsEvent;
          console.log("trade", data);
          if (data.players) setAllPlayerInfo(data.players);
          break;
        }
        case('block-trade'): {
          const data = _data as BlockTradeEvent;
          console.log("block", data);
          break;
        }
        case('flip-all-cards'): {

          const callFlyText = (key: ResultModalType) => {
            //add a delay before text on deck cut to see new card
            if (didCutDeckRef.current) setTimeout(() => setResultModal(key), 300);
            else setResultModal(key);
          }

          SoundManager.playGameSound("flip_all_cards");

          const data = _data as FlipAllCardsEvent;
          let alreadyDead = false;
          console.log("FLIP", data);
          setGame(data.game);
          setIsNextDealer(data.data.nextDealer?.userId === userRef.current.id);

          if (data.data.players) {
            //check if player is already dead before updating players
            if (currentPlayerRef.current && currentPlayerRef.current.lives < 1) alreadyDead = true;
            setAllPlayerInfo(data.data.players);
          }
          setCurrentLosers(data.data.losers);

          let selfWon = false;

          if (data.data.tieReset) {
            callFlyText('tie');
            break;
          }

          //check for winner (and self winner)
          if (data.data.winner) {
            setWinner(data.data.winner);
            if (data.data.winner.userId === userRef.current.id) {
              selfWon = true;
              callFlyText('win');
            }
          }

          //if player is already dead, only show winner modal
          if (alreadyDead) {
            if (data.data.winner) callFlyText("other-win");
            break;
          }

          const selfLoser = data.data.losers.find(loser => loser.userId === userRef.current?.id);
          console.log("self loser", selfLoser);
          
          if (selfLoser && !selfWon) {
            //if you died (and if other player won at same time)
            if (selfLoser.lives < 1) callFlyText(data.data.winner ? "dead-win" : 'dead');
            //if you lost a life
            else callFlyText('lose');
          }
          else if (!data.data.winner) {
            callFlyText('safe');
          }
          break;
        }
      }
    }

    const handleResetGame = (players: GameResetReturn) => {
      setTimeout(() => {
        //wait 2 seconds for winner modal, etc.
        setAllPlayerInfo(players);
        console.log('reset players', players);
        clearGame();
      }, 2000);
    }

    const handleNewEventLog = (data: EventLog) => {
      setEventLogs((prev) => {
        return [...prev, data];
      })
    }

    socket.connect();

    socket.on('connect', initialConnect);
    socket.on(socketKeys.toClient.userInitialized, handleInitialConnect);
    socket.on(socketKeys.toClient.user.exists, handleUserExists);
    socket.on(socketKeys.toClient.user.updated, handleUpdatedUser);
    socket.on(socketKeys.toClient.room.created, handleRoomCreated);
    socket.on(socketKeys.toClient.room.userJoined, handleUserJoin);
    socket.on(socketKeys.toClient.room.userLeft, handleUserLeft);
    socket.on(socketKeys.toClient.room.userDisconnected, handleUserDisconnect);
    socket.on(socketKeys.toClient.room.requiresPassword, triggerPasswordModal);
    
    socket.on(socketKeys.toClient.game.started, handleStartGame);
    socket.on(socketKeys.toClient.game.alreadyStarted, handleAlreadyStarted);
    socket.on(socketKeys.toClient.game.newRound, handleNewRound);
    socket.on(socketKeys.toClient.game.roundEvent, handleGameRoundEvent);
    socket.on(socketKeys.toClient.game.reset, handleResetGame);

    socket.on(socketKeys.toClient.eventLog, handleNewEventLog);
    
    socket.on(socketKeys.toClient.error, handleError);
    console.log('initial CONTEXT');

    return () => {
      socket.off('connect', initialConnect);
      socket.off(socketKeys.toClient.userInitialized, handleInitialConnect);
      socket.off(socketKeys.toClient.user.exists, handleUserExists);
      socket.off(socketKeys.toClient.user.updated, handleUpdatedUser);
      socket.off(socketKeys.toClient.room.created, handleRoomCreated);
      socket.off(socketKeys.toClient.room.userJoined, handleUserJoin);
      socket.off(socketKeys.toClient.room.userLeft, handleUserLeft);
      socket.off(socketKeys.toClient.room.userDisconnected, handleUserDisconnect);
      socket.off(socketKeys.toClient.room.requiresPassword, triggerPasswordModal);
      
      socket.off(socketKeys.toClient.game.started, handleStartGame);
      socket.off(socketKeys.toClient.game.alreadyStarted, handleAlreadyStarted);
      socket.off(socketKeys.toClient.game.newRound, handleNewRound);
      socket.off(socketKeys.toClient.game.roundEvent, handleGameRoundEvent);
      socket.off(socketKeys.toClient.game.reset, handleResetGame);

      socket.off(socketKeys.toClient.eventLog, handleNewEventLog);
      
      socket.off(socketKeys.toClient.error, handleError);
      console.log('disc CONTEXT');
      socket.disconnect();
    }
  }, []);

  const clearGame = () => {
    setGame(null);
    setCurrentCard(null);
    setCurrentLosers([]);
    setCurrentPlayer(null);
    setYourTurn(false);
    setIsDealer(false);
    setIsNextDealer(false);
    setCurrentDealer(null);
  }

  const createRoom = (password?: string) => {
    if (!user) return;
    socket.emit(socketKeys.toServer.room.create, {userId: user.id, password});
  }

  const joinRoom = (code: string, password?: string) => {
    if (!user) return;
    socket.emit(socketKeys.toServer.room.join, {userId: user.id, roomCode: code, password});
    setJoinIntention(null);
  }

  const rejoinRoom = () => {
    if (!userRef.current) return;
    if (userRef.current.createdRoom || userRef.current.joinedRoom) {
      const rejoinData: JoinRoomPayload = {
        userId: userRef.current.id,
        roomCode: (userRef.current.createdRoom || userRef.current.joinedRoom)!
      }
      socket.emit(socketKeys.toServer.room.rejoin, rejoinData);
    }
  }

  const triggerPasswordModal = (code: string) => {
    setPasswordModal(code);
  }

  const addBotToRoom = () => {
    if (!user) return;
    socket.emit(socketKeys.toServer.room.addBot, {userId: user.id, roomCode: room?.code});
  }

  const leaveRoom = (skipEmit?: boolean) => {
    if (!userRef.current) return;
    const code = room ? room.code : (userRef.current.joinedRoom || userRef.current.createdRoom);
    if (skipEmit !== true) socket.emit(socketKeys.toServer.room.leave, {userId: userRef.current.id, roomCode: code});
    clearGame();
    setRoom(null);
    setAllPlayerInfo({players: [], spectators: []});
    navigate('/');
  }

  const kickPlayer = (kickUserId: string) => {
    if (!user) return;
    socket.emit(socketKeys.toServer.room.kick, {userId: user.id, roomCode: room?.code, kickUserId});
  }
  
  const makeGameChoice = (choice: PlayerAction | DealerAction, cutPosition?: number) => {
    console.log("making choice", choice);
    
    if (!user || !room || !yourTurn) return;
    console.log("making choice 2", choice);
    const baseEvent = { userId: user.id, roomCode: room.code };
    
    if (isDealer) {
      setIsCuttingDeck(false);
      if (choice === 'cut') didCutDeckRef.current = true;
      const choiceEvent: ServerRoundEventPayload = {
        ...baseEvent,
        event: ServerRoundEvents[choice === 'cut' ? 'dealerCut' : 'dealerStay'],
        data: cutPosition
      }
      socket.emit(socketKeys.toServer.game.roundEvent, choiceEvent);
      return;
    }
    
    const choiceEvent: ServerRoundEventPayload = {
      ...baseEvent,
      event: ServerRoundEvents[choice === 'swap' ? 'playerSwap' : 'playerStay']
    }
    socket.emit(socketKeys.toServer.game.roundEvent, choiceEvent);
  }

  const updateUserFacing = (direction: PlayerFacingDirection) => {
    if (!roomRef.current || !userRef.current) return;

    const eventData: PlayerFacingPayload = {
      userId: userRef.current.id, roomCode: roomRef.current.code, direction
    }

    socket.emit(socketKeys.toServer.game.updateFacing, eventData);
  }

  const sendEmote = (emoteSlug: PlayerEmoteSlug) => {
    if (!roomRef.current || !userRef.current) return;

    if (emoteLastSentRef.current && (Date.now()/1000)-4 < emoteLastSentRef.current) {
      requestToast("Emote on cooldown.", 'warn');
      return;
    }

    const eventData: PlayerEmotePayload = {
      userId: userRef.current.id, roomCode: roomRef.current.code, emote: emoteSlug
    }
    socket.emit(socketKeys.toServer.game.sendEmote, eventData);
    emoteLastSentRef.current = Date.now()/1000;
  }
  
  const startGame = () => {
    if (!user || !room) return;
    
    if (room.creator !== user.id) return;
    
    const eventData: ServerStartGamePayload = {
      userId: user.id, roomCode: room.code, event: 'start-game', 
      config: {lives: gameConfig.lives, turnTimer: gameConfig.turnTimer}
    }
    
    socket.emit(socketKeys.toServer.game.roundEvent, eventData);
  }
  
  const dealCards = () => {
    if (!user || !room || !game) return;
    if (!isNextDealerRef.current) return;
    
    const eventData: ServerRoundEventPayload = {
      userId: user.id, roomCode: room.code, event: 'deal'
    }
    
    socket.emit(socketKeys.toServer.game.roundEvent, eventData);
  }

  const startSelfTurnTimer = () => {
    if (!userRef.current || !roomRef.current || !gameRef.current) return;

    if (turnTimerRef.current) clearTimeout(turnTimerRef.current);

    console.log('turn timer started');
    turnTimerRef.current = setTimeout(() => {
      console.log(`Turn timer is up`);
      socket.emit(socketKeys.toServer.game.turnTimerEnded, {
        roomCode: roomRef.current?.code,
        userId: userRef.current?.id
      });
    }, gameRef.current.config.turnTimer);
  }
  
  type TimersMap = Map<string, NodeJS.Timeout>;
  const timers = useRef<TimersMap>(new Map());

  const startDisconnectTimer = (userId: string) => {
    if (!userRef.current || !roomRef.current || !gameRef.current) return;
    // Clear any existing timer for the user
    clearDisconnectTimer(userId);
    console.log("starting timer");

    //start timer
    const timer = setTimeout(() => {
      console.log(`User ${userId} disconnect timer completed`);
      socket.emit(socketKeys.toServer.game.disconnectTimerEnded, {
        roomCode: roomRef.current?.code,
        userId: userRef.current?.id,
        leftUserId: userId
      });
      timers.current.delete(userId);
    }, DISCONNECT_TIMER);

    //store the timer in the map
    timers.current.set(userId, timer);
  }

  const clearDisconnectTimer = (userId: string) => {
    const timer = timers.current.get(userId);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(userId);
      console.log(`User ${userId} reconnected. Timer cleared.`);
    }
  }

  return (
    <SocketContext.Provider value={{
      socket,
      isInitializing,
      user: user,
      room,
      game,
      currentPlayer,
      currentPlayerRef,
      currentCard,
      gameConfig,
      setGameConfig,
      yourTurn,
      isDealer,
      isNextDealer,
      currentDealer,
      isCuttingDeck,
      setIsCuttingDeck,
      players: allPlayerInfo.players,
      allPlayerInfoRef,
      spectators: allPlayerInfo.spectators,
      eventLogs,
      currentLosers,
      winner,
      resultModal,
      setResultModal,
      passwordModal,
      setPasswordModal,
      suitImages,
      setSuitImages,
      recentEventLogs,
      joinIntention,
      setJoinIntention,
      createRoom,
      joinRoom,
      rejoinRoom,
      leaveRoom,
      startGame,
      dealCards,
      makeGameChoice,
      addBotToRoom,
      kickPlayer,
      updateUserFacing,
      sendEmote
    }}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook to access the socket context
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
